import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "./utils";

// Mirror of backend FIRE_UNIT_TYPES — controls whether the form renders the
// EMS Crew 1-4 fields or the Fire chauffeur/officer/nozzle/backup/control set.
const FIRE_UNIT_TYPES = new Set(["ENGINE", "BRUSH FIRE UNIT", "BATTALION CHIEF"]);
const isFireUnit = (u) => FIRE_UNIT_TYPES.has(((u && u.unit_type) || "").toUpperCase());

export function VACRCCTab({ units, agencies }) {
  const [allUnits, setAllUnits] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [csls, setCsls] = useState([]);

  // Form state
  const [selectedUnit, setSelectedUnit] = useState("");
  const [unitType, setUnitType] = useState("");
  const [crew1, setCrew1] = useState("");
  const [crew2, setCrew2] = useState("");
  const [crew3, setCrew3] = useState("");
  const [crew4, setCrew4] = useState("");
  // ─── Fire-side crew positions ─────────────────────────────────────────
  const [chauffeur, setChauffeur] = useState("");
  const [officer, setOfficer] = useState("");
  const [nozzle, setNozzle] = useState("");
  const [backup, setBackup] = useState("");
  const [control, setControl] = useState("");
  // Fire radios — 5 slots per spec.
  const [radio1, setRadio1] = useState("");
  const [radio2, setRadio2] = useState("");
  const [radio3, setRadio3] = useState("");
  const [radio4, setRadio4] = useState("");
  const [radio5, setRadio5] = useState("");
  const [crossStreet, setCrossStreet] = useState("");
  const [serviceFrom, setServiceFrom] = useState("");
  const [serviceTo, setServiceTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [validationError, setValidationError] = useState("");
  // NO CAD mode — when checked, the VAC/VFD RCC form actually LOGS THE UNIT ON
  // (logged_in=True, shows on Dispatch Board) instead of just storing
  // validation data. Used when an ambulance has no in-vehicle MDT.
  const [noCad, setNoCad] = useState(false);

  // Authorized Crew Roster modal — lets the dispatcher pick which users are
  // permitted on the selected unit. Persisted server-side on the unit
  // record (`authorized_crew_ids`). When empty → full sister-agency pool.
  const [showAuthCrewModal, setShowAuthCrewModal] = useState(false);
  const [authCrewSelection, setAuthCrewSelection] = useState([]);
  const [authCrewSaving, setAuthCrewSaving] = useState(false);

  const fetchData = useCallback(() => {
    Promise.all([
      axios.get(`${API}/units`),
      axios.get(`${API}/users`),
      axios.get(`${API}/csl`),
    ]).then(([u, usr, c]) => {
      setAllUnits(u.data);
      // Iter 108 — Sort every crew list alphabetically by last name, then
      // first name. Every downstream view (agencyUsers, authCrewCandidates,
      // Fire crew dropdowns, EMS crew dropdowns) reads from this array
      // so one sort here is enough.
      setAllUsers([...usr.data].sort((a, b) => {
        const al = (a.last_name || "").toLowerCase();
        const bl = (b.last_name || "").toLowerCase();
        if (al !== bl) return al.localeCompare(bl);
        return (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase());
      }));
      setCsls(c.data);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Get the selected unit's agency for validation
  const displayUnits = units && units.length > 0 ? units : allUnits;
  const selectedUnitData = displayUnits.find((u) => u.id === selectedUnit);
  const selectedAgency = selectedUnitData?.agency || "";

  // MID-TOUR CREW SWAP mode — the dispatcher clicked an in-service unit to
  // modify its crew mid-shift. In this mode the submit preserves the unit's
  // current logged-on state (the unit does NOT get booted off the board and
  // is NOT affected if they're in the middle of a call). Detected on the
  // frontend so we can relabel the submit button + show a clear banner.
  const midTourSwap = !!selectedUnitData?.logged_in;

  // Sister-agency sharing — BSVAC and Central Brooklyn VAC share personnel.
  // When a unit belongs to either agency, crew members from BOTH agencies
  // are valid for logon on the VAC RCC tab (matches MDT CADLOGON behavior).
  const SISTER_AGENCIES_MAP = {
    "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS": "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS",
    "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS": "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS",
  };
  const sisterAgency = SISTER_AGENCIES_MAP[selectedAgency] || "";
  const effectiveAgencies = [selectedAgency, sisterAgency].filter(Boolean);
  const agencyAllowsUser = (u) => {
    if (!selectedAgency) return true;
    if (effectiveAgencies.includes(u.agency)) return true;
    return Array.isArray(u.agencies) && u.agencies.some((a) => effectiveAgencies.includes(a));
  };

  // Users eligible for this unit: authorized_crew_ids override if set, else
  // the full sister-agency pool. Also filter to AMBULANCE-assignment users
  // (no dispatcher-only users in the crew list).
  //
  // Iter 115 — must be EXPLICITLY assigned AMBULANCE. Empty assignments
  // no longer count so dispatcher-only rosters don't leak into the crew
  // dropdown.
  const authorizedIds = selectedUnitData?.authorized_crew_ids || [];
  const agencyUsers = allUsers.filter((u) => {
    if (!agencyAllowsUser(u)) return false;
    const assigns = (u.assignments || []).map((a) => (a || "").toUpperCase());
    const isAmbulanceEligible = assigns.includes("AMBULANCE");
    if (!isAmbulanceEligible) return false;
    if (authorizedIds.length > 0) return authorizedIds.includes(u.id);
    return true;
  });

  // Validate crew belongs to the same agency (or sister) as the unit
  const fireMode = isFireUnit(selectedUnitData);

  const validateCrewUnit = () => {
    if (!selectedUnit) return "Please select a unit";
    if (fireMode) {
      // Fire — Chauffeur (driver) and Officer are mandatory; the back of
      // the rig (Nozzle / Backup / Control) varies per response.
      if (!chauffeur) return "Chauffeur is required";
      if (!officer) return "Officer is required";
    } else {
      if (!crew1) return "Crew 1 is required";
    }

    const crewNames = fireMode
      ? [chauffeur, officer, nozzle, backup, control].filter(Boolean)
      : [crew1, crew2, crew3, crew4].filter(Boolean);
    for (const name of crewNames) {
      const user = allUsers.find((u) => `${u.first_name} ${u.last_name}` === name);
      if (user && selectedAgency && !agencyAllowsUser(user)) {
        return `${name} does not belong to ${selectedAgency}${sisterAgency ? ` or ${sisterAgency}` : ""}`;
      }
    }
    // Tour times are MANDATORY per operator spec — every unit must declare
    // a service window when going on duty. Enforce strict 24-hour format.
    const timeRe = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!serviceFrom) return "Tour time FROM is required (24-hour HH:MM)";
    if (!timeRe.test(serviceFrom)) return "Tour time FROM must be 24-hour HH:MM (00:00–23:59)";
    if (!serviceTo) return "Tour time TO is required (24-hour HH:MM)";
    if (!timeRe.test(serviceTo)) return "Tour time TO must be 24-hour HH:MM (00:00–23:59)";
    return "";
  };

  const handleSubmit = async () => {
    const error = validateCrewUnit();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/vac-rcc/logon`, {
        unit_id: selectedUnit,
        unit_type: unitType,
        // Always send EMS crew fields (fall back to fire positions when in
        // fire mode so unit.crew is populated for downstream consumers).
        crew_1: fireMode ? chauffeur : crew1,
        crew_2: fireMode ? officer : crew2,
        crew_3: fireMode ? nozzle : crew3,
        crew_4: fireMode ? backup : crew4,
        chauffeur, officer, nozzle, backup, control,
        radio_1: radio1, radio_2: radio2, radio_3: radio3,
        radio_4: radio4, radio_5: radio5,
        cross_street_location: crossStreet,
        service_time_from: serviceFrom,
        service_time_to: serviceTo,
        no_cad: noCad,
      });
      if (res.data?.error) {
        // Backend rejected (personnel/radio conflict, unit not found, etc.)
        setValidationError(res.data.error);
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      setSelectedUnit("");
      setUnitType("");
      setCrew1(""); setCrew2(""); setCrew3(""); setCrew4("");
      setChauffeur(""); setOfficer(""); setNozzle(""); setBackup(""); setControl("");
      setRadio1(""); setRadio2(""); setRadio3(""); setRadio4(""); setRadio5("");
      setCrossStreet("");
      setServiceFrom("");
      setServiceTo("");
      setNoCad(false);
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || "Submission failed. Please try again.";
      setValidationError(typeof msg === "string" ? msg : "Submission failed.");
    }
    setSubmitting(false);
  };

  const handleCancel = () => {
    setSelectedUnit("");
    setUnitType("");
    setCrew1(""); setCrew2(""); setCrew3(""); setCrew4("");
    setChauffeur(""); setOfficer(""); setNozzle(""); setBackup(""); setControl("");
    setRadio1(""); setRadio2(""); setRadio3(""); setRadio4(""); setRadio5("");
    setCrossStreet("");
    setServiceFrom("");
    setServiceTo("");
    setValidationError("");
  };

  // Auto-insert a colon as the dispatcher types 4 digits: "0830" → "08:30".
  // Handles backspace gracefully (stripping a trailing ":" after deleting the
  // last minute digit). Also accepts paste of already-formatted values.
  const autoColonTime = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };
  const handleTimeChange = (setter) => (e) => setter(autoColonTime(e.target.value));

  // Clear crew selections when unit changes (agency may change). When the
  // selected unit is already logged on in the field, pre-fill the form with
  // the unit's current crew/radios/tour so the dispatcher can quickly tweak
  // and re-submit (mid-tour crew swap).
  const handleUnitChange = (e) => {
    const newId = e.target.value;
    setSelectedUnit(newId);
    setValidationError("");
    const src = (units && units.length > 0 ? units : allUnits).find((u) => u.id === newId);
    if (src && src.logged_in && src.crew) {
      const c = src.crew || {};
      setCrew1(c.crew_1 || ""); setCrew2(c.crew_2 || ""); setCrew3(c.crew_3 || ""); setCrew4(c.crew_4 || "");
      setChauffeur(c.chauffeur || ""); setOfficer(c.officer || "");
      setNozzle(c.nozzle || ""); setBackup(c.backup || ""); setControl(c.control || "");
      setRadio1(c.radio_1 || ""); setRadio2(c.radio_2 || ""); setRadio3(c.radio_3 || "");
      setRadio4(c.radio_4 || ""); setRadio5(c.radio_5 || "");
      setUnitType(src.unit_level || "");
      setCrossStreet(src.cross_street_location || "");
      setServiceFrom(src.service_time_from || "");
      setServiceTo(src.service_time_to || "");
      // Preserve the NO CAD mode — the unit stays a NO-CAD unit on re-logon.
      setNoCad(!!src.no_cad);
    } else {
      setCrew1(""); setCrew2(""); setCrew3(""); setCrew4("");
      setChauffeur(""); setOfficer(""); setNozzle(""); setBackup(""); setControl("");
      setRadio1(""); setRadio2(""); setRadio3(""); setRadio4(""); setRadio5("");
      // Critical: reset NO CAD when picking a different / unlogged unit so the
      // checkbox state from the previously-selected unit doesn't leak into
      // this fresh entry. Without this reset, a dispatcher who logs unit A
      // with NO CAD checked, then switches to unit B, would silently submit
      // unit B as NO CAD too.
      setNoCad(!!(src && src.no_cad));
    }
  };

  // Open the Authorized Crew modal for the selected unit — pre-populates
  // with the unit's current roster (or empty if none saved yet).
  const openAuthCrewModal = () => {
    if (!selectedUnit || !selectedUnitData) return;
    setAuthCrewSelection(selectedUnitData.authorized_crew_ids || []);
    setShowAuthCrewModal(true);
  };

  const toggleAuthCrewMember = (userId) => {
    setAuthCrewSelection((prev) => (
      prev.includes(userId) ? prev.filter((x) => x !== userId) : [...prev, userId]
    ));
  };

  const saveAuthCrew = async () => {
    if (!selectedUnit) return;
    setAuthCrewSaving(true);
    try {
      await axios.put(`${API}/units/${selectedUnit}/authorized-crew`, { user_ids: authCrewSelection });
      await fetchData();
      setShowAuthCrewModal(false);
    } catch {}
    setAuthCrewSaving(false);
  };

  // Candidate list for the modal — sister-agency users assigned to
  // AMBULANCE for the selected unit's agency. Iter 115: dispatcher-only
  // members are hidden from the whitelist picker so a chief can't
  // accidentally authorize a non-crew user onto a rig.
  const authCrewCandidates = allUsers.filter((u) => {
    if (!agencyAllowsUser(u)) return false;
    const assigns = (u.assignments || []).map((a) => (a || "").toUpperCase());
    return assigns.includes("AMBULANCE");
  });

  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "#0a0a0a" }}>
      <div data-testid="vac-rcc-form" style={{
        width: 560, border: "1px solid #5577AA",
        borderRadius: 4, overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(85,119,170,0.3)",
        fontFamily: "'Segoe UI', Tahoma, sans-serif",
      }}>
        {/* Title bar */}
        <div style={{
          background: "linear-gradient(180deg, #4488CC 0%, #2266AA 50%, #1A5599 100%)",
          padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ color: "#FFF", fontSize: 18, fontWeight: 600, fontStyle: "italic", textShadow: "1px 1px 2px rgba(0,0,0,0.3)" }}>
            Volunteer Resource Coordination Center
          </span>
          <div style={{ display: "flex", gap: 4 }}>
            <div style={{ width: 22, height: 22, background: "linear-gradient(180deg, #5599DD, #3377BB)", border: "1px solid #2266AA", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12, color: "#FFF", fontWeight: 700 }}>_</div>
            <div style={{ width: 22, height: 22, background: "linear-gradient(180deg, #5599DD, #3377BB)", border: "1px solid #2266AA", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 11, color: "#FFF" }}>&#9633;</div>
            <div style={{ width: 22, height: 22, background: "linear-gradient(180deg, #DD4444, #BB2222)", border: "1px solid #992222", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13, color: "#FFF", fontWeight: 700 }}>X</div>
          </div>
        </div>

        {/* Form body */}
        <div style={{ background: "linear-gradient(180deg, #ECF0F6 0%, #D8DDE8 100%)", padding: "20px 32px 24px" }}>
          {/* ─── In-Service Units sidebar ─── dispatchers click a unit to
               load it into the form pre-filled for a mid-tour crew swap or
               re-logon. Shows every unit currently logged_in. */}
          {(() => {
            const inService = displayUnits.filter((u) => u.logged_in);
            if (inService.length === 0) return null;
            return (
              <div style={{
                marginBottom: 16, padding: "10px 12px",
                background: "#1a2a3a", border: "1px solid #2a4a6a", borderRadius: 4,
                color: "#FFF",
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6, color: "#A8C0E0" }}>
                  IN-SERVICE UNITS — click a unit to swap crew mid-tour
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {inService.map((u) => {
                    const crew = u.crew || {};
                    const cLine = [crew.crew_1, crew.crew_2, crew.crew_3, crew.crew_4].filter(Boolean).join(", ");
                    const active = selectedUnit === u.id;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        data-testid={`vac-inservice-${u.name}`}
                        onClick={() => handleUnitChange({ target: { value: u.id } })}
                        style={{
                          background: active ? "linear-gradient(180deg, #52A85C, #2E7A3A)" : "#2a3a4a",
                          color: "#FFF",
                          border: `1px solid ${active ? "#1E5A24" : "#4a5a6a"}`,
                          padding: "6px 10px", borderRadius: 3,
                          fontSize: 11, fontWeight: 600, cursor: "pointer",
                          textAlign: "left", minWidth: 180,
                          fontFamily: "'Segoe UI', Tahoma, sans-serif",
                        }}
                        title={cLine || "No crew yet"}
                      >
                        <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                          {u.name}
                          {u.no_cad && <span style={{ fontSize: 8, background: "#CC2222", padding: "1px 4px", borderRadius: 2, letterSpacing: 0.5 }}>NO CAD</span>}
                        </div>
                        <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>{cLine || "(no crew assigned)"}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#1a2a3a", letterSpacing: 1 }}>UNIT LOG ON INFORMATION</span>
          </div>
          <div style={{ height: 1, background: "#B8C0D0", marginBottom: 16 }} />

          {/* UNIT */}
          <FormRow label="UNIT:">
            <select data-testid="vac-unit" value={selectedUnit} onChange={handleUnitChange} style={selectStyle}>
              <option value="">Select Unit...</option>
              {displayUnits.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.unit_type || "AMBULANCE"}) - {u.agency}</option>
              ))}
            </select>
          </FormRow>

          {/* MID-TOUR CREW SWAP banner — shown whenever the selected unit is
              already logged-in. Makes it explicit that the dispatcher is
              modifying an active in-service unit (not starting a new shift). */}
          {midTourSwap && (
            <div data-testid="vac-mid-tour-banner" style={{
              marginBottom: 14, padding: "10px 14px",
              background: "linear-gradient(180deg, #FFF2C9 0%, #FFE39A 100%)",
              border: "2px solid #B8860B", borderRadius: 4,
              color: "#5C3D00", fontSize: 12, lineHeight: 1.4,
            }}>
              <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.3, marginBottom: 2 }}>
                MID-TOUR CREW SWAP MODE — {selectedUnitData?.name}
              </div>
              <div>
                This unit is already in service. Changes below will UPDATE the crew on the logged-in unit without booting them off the board. If the unit is on a call, the call state is preserved.
              </div>
            </div>
          )}

          {/* UNIT TYPE */}
          <FormRow label="UNIT TYPE:">
            <select data-testid="vac-unit-type" value={unitType} onChange={(e) => setUnitType(e.target.value)} style={selectStyle}>
              <option value="">Select...</option>
              <option value="BLS">BLS</option>
              <option value="ALS">ALS</option>
            </select>
          </FormRow>

          {/* Agency indicator + authorized-crew roster editor */}
          {selectedAgency && (
            <div style={{ marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, color: "#336", fontWeight: 600, gap: 10 }}>
              <div>
                Showing crew members for: {selectedAgency}
                {sisterAgency && <span style={{ color: "#559", marginLeft: 6 }}>+ {sisterAgency}</span>}
                {authorizedIds.length > 0 && (
                  <span style={{ color: "#884400", marginLeft: 6 }}>
                    (roster restricted to {authorizedIds.length} member{authorizedIds.length === 1 ? "" : "s"})
                  </span>
                )}
              </div>
              <button
                data-testid="vac-edit-auth-crew"
                type="button"
                onClick={openAuthCrewModal}
                style={{
                  background: "linear-gradient(180deg, #E8E8F0, #C8C8D0)",
                  border: "1px solid #99AABB", borderRadius: 3,
                  padding: "4px 10px", fontSize: 11, fontWeight: 700,
                  color: "#243", cursor: "pointer", whiteSpace: "nowrap",
                }}>
                EDIT AUTHORIZED CREW
              </button>
            </div>
          )}

          {/* ─── Authorized Crew modal ─── */}
          {showAuthCrewModal && (
            <div
              data-testid="vac-auth-crew-modal"
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center" }}
              onClick={(e) => { if (e.target === e.currentTarget) setShowAuthCrewModal(false); }}>
              <div style={{ width: 460, maxHeight: "85vh", display: "flex", flexDirection: "column", background: "#ECF0F6", border: "2px solid #2266AA", borderRadius: 4, overflow: "hidden", fontFamily: "'Segoe UI', Tahoma, sans-serif" }}>
                <div style={{ background: "linear-gradient(180deg, #4488CC, #2266AA)", padding: "8px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: "#FFF", fontSize: 14, fontWeight: 700 }}>Authorized Crew — {selectedUnitData?.name}</span>
                  <button
                    data-testid="vac-auth-crew-close"
                    type="button"
                    onClick={() => setShowAuthCrewModal(false)}
                    style={{ width: 22, height: 22, background: "#CC3333", border: "1px solid #AA2222", color: "#FFF", borderRadius: 3, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>X</button>
                </div>
                <div style={{ padding: "10px 14px", fontSize: 12, color: "#345", borderBottom: "1px solid #B8C0D0", background: "#F4F6FA" }}>
                  Check the crew members allowed on this unit. When any are checked, ONLY those members appear in the Crew 1–4 dropdowns on both the VAC RCC tab and the MDT CADLOGON. Leave everything unchecked to allow all {sisterAgency ? "sister-agency" : "agency"} personnel.
                </div>
                <div style={{ flex: 1, overflow: "auto", padding: "8px 14px", background: "#FFF" }}>
                  {authCrewCandidates.length === 0 && (
                    <div style={{ padding: 12, color: "#888", fontSize: 12, textAlign: "center" }}>No eligible crew members for this agency.</div>
                  )}
                  {authCrewCandidates.map((u) => {
                    const checked = authCrewSelection.includes(u.id);
                    return (
                      <label key={u.id}
                        data-testid={`vac-auth-crew-row-${u.id}`}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 4px", borderBottom: "1px solid #EEE", cursor: "pointer", fontSize: 13 }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleAuthCrewMember(u.id)} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: "#223" }}>{u.first_name} {u.last_name}</div>
                          <div style={{ fontSize: 10, color: "#789" }}>
                            {(u.agency || "—").substring(0, 40)}
                            {(u.certifications || []).length > 0 && ` · ${(u.certifications || []).join(", ")}`}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #B8C0D0", background: "#E8EDF5" }}>
                  <button
                    data-testid="vac-auth-crew-clear"
                    type="button"
                    onClick={() => setAuthCrewSelection([])}
                    style={{ background: "#FFF", border: "1px solid #AAB", padding: "5px 10px", fontSize: 11, cursor: "pointer", color: "#666" }}>
                    Clear (allow all)
                  </button>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      data-testid="vac-auth-crew-cancel"
                      type="button"
                      onClick={() => setShowAuthCrewModal(false)}
                      style={{ background: "#DDE2EA", border: "1px solid #99AABB", padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button
                      data-testid="vac-auth-crew-save"
                      type="button"
                      disabled={authCrewSaving}
                      onClick={saveAuthCrew}
                      style={{ background: authCrewSaving ? "#888" : "linear-gradient(180deg, #52A85C, #2E7A3A)", border: "1px solid #1E5A24", color: "#FFF", padding: "6px 18px", fontSize: 12, fontWeight: 700, cursor: authCrewSaving ? "not-allowed" : "pointer" }}>
                      {authCrewSaving ? "Saving..." : "Save Roster"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Crew positions — Fire mode shows 5 named positions; EMS keeps Crew 1–4. */}
          {fireMode ? (
            <>
              <div style={{ marginBottom: 8, padding: "6px 12px", background: "#FFE0CC", border: "1px solid #C09060", borderRadius: 3, fontSize: 12, fontWeight: 700, color: "#7A3A00", textAlign: "center" }} data-testid="vac-fire-mode-banner">
                FIRE APPARATUS — staffing positions per operator spec
              </div>
              <FormRow label="CHAUFFEUR:">
                <select data-testid="vac-chauffeur" value={chauffeur} onChange={(e) => setChauffeur(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
              <FormRow label="OFFICER:">
                <select data-testid="vac-officer" value={officer} onChange={(e) => setOfficer(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
              <FormRow label="NOZZLE:">
                <select data-testid="vac-nozzle" value={nozzle} onChange={(e) => setNozzle(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
              <FormRow label="BACKUP:">
                <select data-testid="vac-backup" value={backup} onChange={(e) => setBackup(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
              <FormRow label="CONTROL:">
                <select data-testid="vac-control" value={control} onChange={(e) => setControl(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
              {/* 5 radio fields — Fire spec. Free-text input keyed off agency
                  radio bank if available, else manual entry. */}
              {[1, 2, 3, 4, 5].map((n) => {
                const valGetters = [radio1, radio2, radio3, radio4, radio5];
                const setters = [setRadio1, setRadio2, setRadio3, setRadio4, setRadio5];
                return (
                  <FormRow key={`radio-${n}`} label={`RADIO ${n}:`}>
                    <input
                      data-testid={`vac-radio${n}`}
                      type="text"
                      value={valGetters[n - 1]}
                      onChange={(e) => setters[n - 1](e.target.value.toUpperCase())}
                      style={inputStyle}
                      placeholder="Radio number / call-sign"
                    />
                  </FormRow>
                );
              })}
            </>
          ) : (
            <>
              {/* CREW 1-4 - filtered by unit's agency */}
              <FormRow label="CREW 1:">
                <select data-testid="vac-crew1" value={crew1} onChange={(e) => setCrew1(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
              <FormRow label="CREW 2:">
                <select data-testid="vac-crew2" value={crew2} onChange={(e) => setCrew2(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
              <FormRow label="CREW 3:">
                <select data-testid="vac-crew3" value={crew3} onChange={(e) => setCrew3(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
              <FormRow label="CREW 4:">
                <select data-testid="vac-crew4" value={crew4} onChange={(e) => setCrew4(e.target.value)} style={selectStyle}>
                  <option value="">Select...</option>
                  {agencyUsers.map((u) => (
                    <option key={u.id} value={`${u.first_name} ${u.last_name}`}>{u.first_name} {u.last_name}</option>
                  ))}
                </select>
              </FormRow>
            </>
          )}

          {/* CROSS STREET LOCATION */}
          <FormRow label="CROSS STREET LOCATION:">
            <select data-testid="vac-csl" value={crossStreet} onChange={(e) => setCrossStreet(e.target.value)} style={selectStyle}>
              <option value="">Select...</option>
              {csls.map((c) => (
                <option key={c.id} value={c.name}>{c.name} {c.borough_coverage ? `(${c.borough_coverage})` : ""}</option>
              ))}
            </select>
          </FormRow>

          {/* TOUR TIME — required per operator spec. 24-hour format
              (00:00–23:59). Both FROM and TO are mandatory. The red asterisk
              + `(required)` marker matches the form's standard label
              styling for required fields. */}
          <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
            <label style={labelStyle}>TOUR TIME<span style={{ color: "#C00000" }}> *</span>:</label>
            <input data-testid="vac-time-from" type="text" inputMode="numeric" pattern="^([01]\d|2[0-3]):[0-5]\d$" maxLength={5} placeholder="HH:MM" title="24-hour format HH:MM (00:00 – 23:59) — REQUIRED" value={serviceFrom} onChange={handleTimeChange(setServiceFrom)} required style={{ ...inputStyle, width: 120, borderColor: serviceFrom ? inputStyle.borderColor : "#C00000" }} />
            <span style={{ padding: "0 10px", fontWeight: 600, color: "#334", fontSize: 14 }}>TO</span>
            <input data-testid="vac-time-to" type="text" inputMode="numeric" pattern="^([01]\d|2[0-3]):[0-5]\d$" maxLength={5} placeholder="HH:MM" title="24-hour format HH:MM (00:00 – 23:59) — REQUIRED" value={serviceTo} onChange={handleTimeChange(setServiceTo)} required style={{ ...inputStyle, width: 120, borderColor: serviceTo ? inputStyle.borderColor : "#C00000" }} />
          </div>

          <div style={{ height: 1, background: "#B8C0D0", margin: "16px 0" }} />

          {/* Validation error */}
          {validationError && (
            <div data-testid="vac-validation-error" style={{
              marginBottom: 12, textAlign: "center", padding: "8px 16px",
              background: "#F2DEDE", border: "1px solid #A94442", borderRadius: 3,
              color: "#A94442", fontSize: 13, fontWeight: 600,
            }}>
              {validationError}
            </div>
          )}

          {/* NO CAD checkbox — flips the form from validation-only into an
              actual logon for ambulances without an in-vehicle MDT. Hidden
              during a mid-tour swap because the unit is already logged on and
              this control wouldn't change anything. */}
          {!midTourSwap && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 8, marginBottom: 10, padding: "6px 12px",
              background: noCad ? "#FFF5CC" : "#F0F4F8",
              border: `1px solid ${noCad ? "#CC9933" : "#BBC5D0"}`,
              borderRadius: 3, fontSize: 12,
            }}>
              <input
                id="vac-no-cad"
                data-testid="vac-no-cad-checkbox"
                type="checkbox"
                checked={noCad}
                onChange={(e) => setNoCad(e.target.checked)}
              />
              <label htmlFor="vac-no-cad" style={{ fontWeight: 700, color: noCad ? "#8B6914" : "#345", cursor: "pointer" }}>
                NO CAD Feature
              </label>
              <span style={{ fontSize: 11, color: noCad ? "#8B6914" : "#678" }}>
                {noCad
                  ? " — submitting this form will LOG THIS UNIT ON (ambulance has no MDT)."
                  : " — check this if the ambulance has no MDT; otherwise this form is validation-only."}
              </span>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
            <button data-testid="vac-submit" onClick={handleSubmit} disabled={submitting || !selectedUnit}
              style={{
                padding: "8px 36px", fontSize: 14, fontWeight: 600, cursor: selectedUnit ? "pointer" : "not-allowed",
                background: selectedUnit
                  ? (midTourSwap
                    ? "linear-gradient(180deg, #E8B94C 0%, #C89024 50%, #A87410 100%)"
                    : noCad
                    ? "linear-gradient(180deg, #E8B94C 0%, #C89024 50%, #A87410 100%)"
                    : "linear-gradient(180deg, #6CB4E8 0%, #3A90D0 50%, #2A7ABC 100%)")
                  : "#AAA",
                border: `1px solid ${(midTourSwap || noCad) ? "#8B6914" : "#2266AA"}`, borderRadius: 3, color: "#FFF",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }}>
              {submitting
                ? "Submitting..."
                : (midTourSwap ? "Update Crew (Mid-Tour Swap)" : (noCad ? "Log Unit On" : "Submit"))}
            </button>
            <button data-testid="vac-cancel" onClick={handleCancel}
              style={{
                padding: "8px 36px", fontSize: 14, fontWeight: 600, cursor: "pointer",
                background: "linear-gradient(180deg, #F0F0F0 0%, #D8D8D8 50%, #C8C8C8 100%)",
                border: "1px solid #999", borderRadius: 3, color: "#333",
                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
              }}>
              Cancel
            </button>
          </div>

          {submitted && (
            <div data-testid="vac-success" style={{
              marginTop: 12, textAlign: "center", padding: "8px 16px",
              background: "#DFF0D8", border: "1px solid #3C763D", borderRadius: 3,
              color: "#3C763D", fontSize: 13, fontWeight: 600,
            }}>
              {midTourSwap ? "Crew updated — unit still in service" : "Unit logged on successfully"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  width: 170, textAlign: "right", paddingRight: 12,
  fontSize: 13, fontWeight: 600, color: "#1a2a3a",
  fontFamily: "'Segoe UI', Tahoma, sans-serif", whiteSpace: "nowrap",
};

const selectStyle = {
  flex: 1, padding: "6px 8px", fontSize: 13,
  border: "1px solid #AAB8CC", borderRadius: 2,
  background: "#FFF", color: "#222",
  fontFamily: "'Segoe UI', Tahoma, sans-serif", outline: "none", cursor: "pointer",
};

const inputStyle = {
  padding: "6px 8px", fontSize: 13,
  border: "1px solid #AAB8CC", borderRadius: 2,
  background: "#FFF", color: "#222",
  fontFamily: "'Segoe UI', Tahoma, sans-serif", outline: "none",
};

function FormRow({ label, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}
